import { formatFacilityWithStateCode } from 'common/utils/facilityLabel';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  Chip,
  Grid,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingModule from 'common/components/LoadingModule';
import useRoles from 'common/hooks/useRoles';
import { loadReversalQueuePage, updateReversalQueueSort } from 'store/thunks/reversalQueueThunks';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { getClaimSplit } from '../utils/allocationClaimSplit';

const HEADER_BG = '#0d1f35';
const PAGE_SIZE = 25;

const HEADER_CELL_SX = {
  bgcolor: HEADER_BG,
  color: '#fff',
  fontWeight: 600,
  fontSize: 13,
  borderBottom: 'none',
};

const BODY_CELL_SX = { height: 60, py: 1 };

const noop = () => {};

const EmptyState = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1 }}>
    <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'grey.400' }} />
    <Typography variant='h6' sx={{ fontWeight: 600 }}>
      No pending decisions
    </Typography>
    <Typography
      variant='body2'
      sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 420 }}
    >
      All reversal items have been resolved. New items will appear here automatically.
    </Typography>
  </Box>
);

const ReversalReviewTable = ({ onManage }) => {
  const dispatch = useDispatch();
  const { resolveAllocation } = useRoles();
  const { pages, currentPage, totalCount, sort } = useSelector(
    ({ inventory }) => inventory.reversalQueue
  );

  const sortOrder = sort?.order ?? 'DESC';

  const handleSortToggle = () =>
    dispatch(
      updateReversalQueueSort({
        sort: { field: 'CREATED_AT', order: sortOrder === 'DESC' ? 'ASC' : 'DESC' },
      })
    );

  const items = pages[currentPage];
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    dispatch(loadReversalQueuePage({ page: 1 }));
  }, [dispatch]);

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;
    dispatch(loadReversalQueuePage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Grid component={Paper} sx={{ width: '100%', overflow: 'hidden' }} container direction='column'>
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>Drug Name</TableCell>
                <TableCell sx={HEADER_CELL_SX}>Lot #</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, textAlign: 'center' }}>Qty</TableCell>
                <TableCell sx={HEADER_CELL_SX}>Facility</TableCell>
                <TableCell
                  sx={{ ...HEADER_CELL_SX, cursor: 'pointer', userSelect: 'none' }}
                  onClick={handleSortToggle}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Created At
                    {sortOrder === 'DESC' ? (
                      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, textAlign: 'center', width: 140 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!isNil(items) &&
                items.map((row) => {
                  const key = `${row.lotNumber}-${row.ndc}-${row.fulfillingPharmacy?.fulfillingPharmacyId}-${row.createdAt}`;
                  return (
                    <TableRow key={key} hover>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant='body2' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {row.drugName}
                        </Typography>
                        <Typography
                          variant='caption'
                          sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}
                        >
                          {row.strength ? `Strength: ${row.strength}` : ''}
                          {row.strength && row.ndc ? ' | ' : ''}
                          {row.ndc ? (
                            <Box component='span' sx={{ fontFamily: 'monospace' }}>
                              NDC: {row.ndc}
                            </Box>
                          ) : (
                            ''
                          )}
                          {row.manufacturer ? ` · Manufacturer: ${row.manufacturer}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, fontFamily: 'monospace' }}>
                        {row.lotNumber}
                        {/* POM-6084 - an unverified claim is a materially different row:
                            restocking it creates verified stock out of a scan nobody has
                            confirmed, permanently. Shown against the lot number because
                            that is the identity the reviewer is judging. */}
                        {row.isUnverified && (
                          <Tooltip
                            title={
                              row.verifiedAt
                                ? `${SYSTEM_DEFICIT} claim — part of this quantity has already been confirmed as received and resolves as ordinary stock`
                                : `${SYSTEM_DEFICIT} claim — inventory has no receiving record for this lot`
                            }
                            arrow
                            placement='top'
                          >
                            <Chip
                              label={
                                row.verifiedAt
                                  ? `${SYSTEM_DEFICIT} · part confirmed`
                                  : SYSTEM_DEFICIT
                              }
                              size='small'
                              sx={{
                                ml: 1,
                                bgcolor: '#fff3e0',
                                color: '#e65100',
                                fontWeight: 700,
                                fontSize: 10,
                                height: 20,
                                fontFamily: 'inherit',
                              }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...BODY_CELL_SX,
                          textAlign: 'center',
                          fontWeight: 600,
                          minWidth: 80,
                        }}
                      >
                        {row.quantity}
                        {/* POM-6152 - the Lot Summary counts only the open deficit, so a
                            part-confirmed row needs it here too or the two screens disagree. */}
                        {(() => {
                          const { openClaim, confirmed } = getClaimSplit(row);
                          if (!row.isUnverified || confirmed === 0) return null;
                          return (
                            <Typography
                              data-testid='reversal-row-open-claim'
                              variant='caption'
                              sx={{ display: 'block', color: 'text.secondary', fontWeight: 500 }}
                            >
                              {openClaim} open
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        {formatFacilityWithStateCode(
                          row.fulfillingPharmacy?.name,
                          row.fulfillingPharmacy?.state
                        ) || '—'}
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        {row.createdAt
                          ? format(new Date(row.createdAt), 'MMM d, yyyy · HH:mm')
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center', width: 140 }}>
                        {resolveAllocation && (
                          <Tooltip
                            title={
                              row.isInPhysicalCount
                                ? 'This item is currently marked as physical count'
                                : ''
                            }
                            arrow
                            placement='top'
                          >
                            {/* span wrapper so the Tooltip still fires on a disabled Button */}
                            <span>
                              <Button
                                variant='outlined'
                                size='small'
                                startIcon={<TuneIcon />}
                                disabled={row.isInPhysicalCount}
                                onClick={() => onManage(row)}
                                sx={{ color: '#0d1f35', borderColor: '#0d1f35' }}
                              >
                                Manage
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>

            {!isNil(items) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', display: 'flex', py: 1 }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(items) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(items) && isEmpty(items) && (
        <Grid item>
          <EmptyState />
        </Grid>
      )}
    </Grid>
  );
};

ReversalReviewTable.propTypes = {
  onManage: PropTypes.func,
};

ReversalReviewTable.defaultProps = {
  onManage: noop,
};

export default ReversalReviewTable;
